import {
  defaultPlanningPreferences,
  normalizePlanningPreferences
} from './planningPreferences';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];
const PERIODS = ['week', 'month', 'year'];

export function emptyMealTypeLimits() {
  return Object.fromEntries(MEAL_TYPES.map((t) => [t, { min: null, max: null }]));
}

export function defaultMealPlanConstraints() {
  return {
    defaultMealTypes: ['dinner'],
    customRules: [],
    globalRules: {
      avoidRecipeIds: [],
      preferTags: [],
      avoidTags: [],
      preferRatings: null,
      maxDuration: null,
      usageTrackingPeriod: { value: 2, unit: 'weeks' },
      maxRecipesPerWeek: null,
      maxRecipesPerMonth: null,
      maxRecipesPerYear: null,
      minPortionsPerWeek: null,
      maxPortionsPerWeek: null,
      minPortionsPerMonth: null,
      maxPortionsPerMonth: null,
      minPortionsPerYear: null,
      maxPortionsPerYear: null,
      mealTypeCounts: Object.fromEntries(PERIODS.map((p) => [p, emptyMealTypeLimits()])),
      mealTypePortions: Object.fromEntries(PERIODS.map((p) => [p, emptyMealTypeLimits()]))
    },
    planningPreferences: defaultPlanningPreferences()
  };
}

function deepMergeGlobalRules(base, override) {
  if (!override) return { ...base };
  const merged = { ...base, ...override };
  if (override.mealTypeCounts) {
    merged.mealTypeCounts = { ...base.mealTypeCounts };
    for (const period of PERIODS) {
      merged.mealTypeCounts[period] = {
        ...(base.mealTypeCounts?.[period] || emptyMealTypeLimits()),
        ...(override.mealTypeCounts[period] || {})
      };
      for (const mealType of MEAL_TYPES) {
        merged.mealTypeCounts[period][mealType] = {
          ...(base.mealTypeCounts?.[period]?.[mealType] || { min: null, max: null }),
          ...(override.mealTypeCounts[period]?.[mealType] || {})
        };
      }
    }
  }
  if (override.mealTypePortions) {
    merged.mealTypePortions = { ...base.mealTypePortions };
    for (const period of PERIODS) {
      merged.mealTypePortions[period] = {
        ...(base.mealTypePortions?.[period] || emptyMealTypeLimits()),
        ...(override.mealTypePortions[period] || {})
      };
      for (const mealType of MEAL_TYPES) {
        merged.mealTypePortions[period][mealType] = {
          ...(base.mealTypePortions?.[period]?.[mealType] || { min: null, max: null }),
          ...(override.mealTypePortions[period]?.[mealType] || {})
        };
      }
    }
  }
  return merged;
}

export function normalizeMealPlanConstraints(input) {
  const defaults = defaultMealPlanConstraints();
  if (!input || typeof input !== 'object') {
    return defaults;
  }
  return {
    defaultMealTypes: Array.isArray(input.defaultMealTypes) && input.defaultMealTypes.length > 0
      ? input.defaultMealTypes
      : defaults.defaultMealTypes,
    customRules: Array.isArray(input.customRules) ? input.customRules : [],
    globalRules: deepMergeGlobalRules(defaults.globalRules, input.globalRules || {}),
    planningPreferences: normalizePlanningPreferences(
      input.planningPreferences || defaults.planningPreferences
    )
  };
}

export function constraintsWithoutUsageTracking(constraints) {
  const normalized = normalizeMealPlanConstraints(constraints);
  const { usageTrackingPeriod, ...globalWithoutTracking } = normalized.globalRules;
  return {
    ...normalized,
    globalRules: globalWithoutTracking
  };
}

export function countCustomRules(constraints) {
  return normalizeMealPlanConstraints(constraints).customRules.length;
}

export function summarizeConstraints(constraints) {
  const normalized = normalizeMealPlanConstraints(constraints);
  const rules = normalized.customRules.length;
  const portions = PERIODS.filter((period) => {
    const key = `minPortionsPer${period.charAt(0).toUpperCase()}${period.slice(1)}`;
    const maxKey = `maxPortionsPer${period.charAt(0).toUpperCase()}${period.slice(1)}`;
    return normalized.globalRules[key] != null || normalized.globalRules[maxKey] != null;
  });
  return { rules, portions: portions.length, mealTypes: normalized.defaultMealTypes };
}
